"use client"

import { CKEditor } from "@ckeditor/ckeditor5-react"
import { useEffect, useRef } from "react"

import {
  Alignment,
  AutoLink,
  BlockQuote,
  Bold,
  ClassicEditor,
  Code,
  Font,
  GeneralHtmlSupport,
  Heading,
  HtmlEmbed,
  Image,
  ImageCaption,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  ImageUpload,
  Italic,
  Link,
  List,
  ListProperties,
  MediaEmbed,
  ShiftEnter,
  SourceEditing,
  Strikethrough,
  Style,
  Table,
  TableCellProperties,
  TableProperties,
  TableToolbar,
  Underline,
} from "ckeditor5"

import { YouTubeThumb } from "./youtube-thumb-plugin"

import "ckeditor5/ckeditor5.css"

import { SymbolPlugin } from "./SymbolPlugin"
import { ImageAlbumPlugin } from "./ImageAlbumPlugin"
import { InsertMediaPlugin, CK_OPEN_MEDIA_EVENT } from "./InsertMediaPlugin"
import { YouTubeEmbedPlugin } from "./YouTubeEmbedPlugin"
import { StrapiUploadAdapterPlugin } from "./StrapiUploadAdapter"

const LICENSE_KEY = "GPL"
const CKBOX_TOKEN_URL = ""

/** Escape tối thiểu để nhúng text thô của user vào 1 chuỗi HTML an toàn. */
function escapeHtml(text: string): string {
  return text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
}

// Style ảnh hưởng typography mà nguồn dán (Word/web/Facebook...) hay kèm theo —
// luôn dọn sạch để nội dung dán ăn theo cỡ chữ/giãn dòng mặc định của bài viết
// (globals.css .ck-content), không giữ nguyên style gốc từ nơi copy.
const PASTE_STYLE_PROPS_TO_STRIP = [
  "font-size",
  "font-family",
  "font",
  "line-height",
  "letter-spacing",
  "margin",
  "margin-top",
  "margin-bottom",
  "padding",
  "padding-top",
  "padding-bottom",
]

/**
 * Dọn style dán từ nguồn ngoài bằng DOM thật — bắt được mọi phần tử kể cả thẻ
 * <font> cũ (Word/Outlook) hoặc style viết tắt (vd "font: 14px Arial"), thay vì
 * đi từng view-node thủ công như trước (hay sót: chỉ bắt font-size/font-family
 * trên <span>, bỏ sót <div> lồng nhau mà nguồn dán tạo ra → chữ dán vào vẫn giữ
 * cỡ gốc + dòng sát nhau vì div không được set line-height/margin).
 * `view` là view fragment ĐÃ được CKEditor tự chuẩn hoá (Word/Google Docs
 * cleanup...) ở bước trước inputTransformation — chỉ dọn thêm style, không dựng
 * lại từ clipboard thô để không mất phần chuẩn hoá đó.
 */
function stripPasteTypography(view: any, editor: ClassicEditor) {
  if (typeof window === "undefined") return view
  const html = editor.data.processor.toData(view)
  if (!html) return view

  const doc = new DOMParser().parseFromString(html, "text/html")
  doc.body.querySelectorAll<HTMLElement>("*").forEach(el => {
    PASTE_STYLE_PROPS_TO_STRIP.forEach(prop => el.style.removeProperty(prop))
    if (el.tagName === "FONT") {
      el.removeAttribute("size")
      el.removeAttribute("face")
      el.removeAttribute("color")
    }
  })

  return editor.data.processor.toView(doc.body.innerHTML)
}

interface CKEditorProps {
  value?: string
  onChange: (data: string) => void
  onEditorReady?: (editor: ClassicEditor) => void
  /** Khi được truyền, hiện nút "Thêm ảnh / video" ngay trong toolbar (sticky). */
  onInsertMedia?: () => void
  /**
   * Ép MỌI thao tác dán thành plain text — mỗi dòng thành 1 <p>, bỏ qua hoàn
   * toàn HTML/bảng mà nguồn dán mang theo. Dùng cho field chỉ chứa danh sách
   * thô (vd EAN ở Campaign): dán 1 cột Excel vào field bình thường sẽ tự tạo
   * <table> (Excel kèm sẵn HTML table trong clipboard, Table plugin bắt lấy),
   * phá vỡ định dạng "mỗi dòng 1 mã" mà phía đọc dữ liệu (split("\n")) cần.
   */
  plainTextPaste?: boolean
}

const CKEditorComponent = ({ value, onChange, onEditorReady, onInsertMedia, plainTextPaste }: CKEditorProps) => {
  const editorRef = useRef<ClassicEditor | null>(null)
  const onInsertMediaRef = useRef(onInsertMedia)
  onInsertMediaRef.current = onInsertMedia

  useEffect(() => {
    if (editorRef.current && value !== undefined && value !== null) {
      const currentData = editorRef.current.getData()
      if (currentData !== value) {
        editorRef.current.setData(value)
      }
    }
  }, [value])

  // Nút "Thêm ảnh / video" trong toolbar dispatch event; React lắng nghe ở đây.
  useEffect(() => {
    if (!onInsertMedia) return
    const handler = () => onInsertMediaRef.current?.()
    window.addEventListener(CK_OPEN_MEDIA_EVENT, handler)
    return () => window.removeEventListener(CK_OPEN_MEDIA_EVENT, handler)
  }, [onInsertMedia])

  return (
    <div className="[&_.ck-editor__editable]:text-[22px]">
      <CKEditor
        editor={ClassicEditor}
        onReady={(editor: ClassicEditor) => {
          editorRef.current = editor

          const clipboardPlugin = editor.plugins.get("ClipboardPipeline")

          clipboardPlugin.on(
            "inputTransformation",
            (evt: any, data: any) => {
              if (plainTextPaste) {
                const plainText: string = data.dataTransfer?.getData?.("text/plain") || ""
                const lines = plainText
                  .split(/\r\n|\r|\n/)
                  .map((line: string) => line.trim())
                  .filter(Boolean)

                // Bỏ qua HTML/bảng nguồn dán mang theo (vd Excel) — luôn dựng lại từ
                // text/plain, mỗi dòng 1 <p>, khớp định dạng phía đọc (split("\n")).
                if (lines.length > 0) {
                  const html = lines.map((line: string) => `<p>${escapeHtml(line)}</p>`).join("")
                  data.content = editor.data.processor.toView(html)
                }
                return
              }

              data.content = stripPasteTypography(data.content, editor)
            },
            { priority: "normal" },
          )

          // CKEditor's HtmlEmbed widget only commits textarea content to the model
          // when the user clicks ✓. If the form is saved while the textarea is still
          // open, getData() returns an empty <div class="raw-html-embed"></div>.
          // This listener auto-syncs every keystroke directly to the model so the
          // content is always persisted regardless of whether ✓ was clicked.
          const editorEl = editor.ui.getEditableElement()
          if (editorEl) {
            editorEl.addEventListener(
              "input",
              (e: Event) => {
                const target = e.target as HTMLElement
                if (!target.classList.contains("raw-html-embed__source")) return

                const widgetEl = target.closest(".ck-widget")
                if (!widgetEl) return

                try {
                  const viewEl = editor.editing.view.domConverter.mapDomToView(widgetEl as HTMLElement)
                  if (!viewEl || !viewEl.is("element")) return

                  const modelEl = editor.editing.mapper.toModelElement(viewEl)
                  if (!modelEl?.is("element", "rawHtml")) return

                  editor.model.change((writer) => {
                    writer.setAttribute("value", (target as HTMLTextAreaElement).value, modelEl)
                  })
                } catch {
                  // ignore mapping errors (e.g. during editor teardown)
                }
              },
              true, // capture phase so we get the event before CKEditor can swallow it
            )
          }

          if (editor && value !== undefined && value !== null) {
            editor.setData(value)
          }
          if (onEditorReady) {
            onEditorReady(editor)
          }
        }}
        onChange={(event: any, editor: ClassicEditor) => {
          const data = editor.getData()
          onChange(data)
        }}
        config={{
          licenseKey: LICENSE_KEY,
          plugins: [
            Font,
            Heading,
            Alignment,
            Image,
            ImageCaption,
            ImageResize,
            ImageToolbar,
            ImageStyle,
            ImageUpload,
            MediaEmbed,
            Link,
            AutoLink,
            Bold,
            Code,
            Italic,
            Strikethrough,
            Underline,
            Table,
            TableToolbar,
            TableProperties,
            TableCellProperties,
            StrapiUploadAdapterPlugin,
            List,
            ListProperties,
            GeneralHtmlSupport,
            HtmlEmbed,
            SourceEditing,
            // ShiftEnter đăng ký model `softBreak` ↔ <br>. Thiếu nó thì mọi <br> (nhập
            // ở Source/HTML, dán, hoặc Shift+Enter) đều bị CKEditor bỏ khi setData.
            ShiftEnter,
            BlockQuote,
            Style,
            // ─── Add Symbol Plugin here ───────────────────────────────────
            SymbolPlugin,
            ImageAlbumPlugin,
            YouTubeEmbedPlugin,
            ...(onInsertMedia ? [InsertMediaPlugin] : []),
            // ─────────────────────────────────────────────────────────────
            YouTubeThumb,
            ...(CKBOX_TOKEN_URL ? ["CKBox"] : []),
            ...(LICENSE_KEY !== "GPL" ? ["SlashCommand"] : []),
          ],
          toolbar: [
            ...(onInsertMedia ? ["insertMedia", "|"] : []),
            "bold",
            "italic",
            "underline",
            "strikethrough",
            "alignment",
            "mediaEmbed",
            "insertYouTubeThumb",
            "insertTable",
            "link",
            "blockQuote",
            "style",
            {
              label: "Fonts",
              icon: false,
              items: ["fontSize", "fontFamily", "fontColor", "fontBackgroundColor"],
            },
            {
              label: "Lists",
              icon: false,
              items: ["bulletedList", "numberedList"],
            },
            "symbolPicker",
            "|",
            "insertImageAlbum",
            "htmlEmbed",
            "sourceEditing",
          ],
          list: {},
          heading: {
            options: [
              { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
              { model: "heading1", view: "h1", title: "Heading 1", class: "ck-heading_heading1" },
              { model: "heading2", view: "h2", title: "Heading 2", class: "ck-heading_heading2" },
              { model: "heading3", view: "h3", title: "Heading 3", class: "ck-heading_heading3" },
            ],
          },
          fontFamily: {
            options: [
              "default",
              "Arial, Helvetica, sans-serif",
              "Courier New, Courier, monospace",
              "Georgia, serif",
              "Lucida Sans Unicode, Lucida Grande, sans-serif",
              "Tahoma, Geneva, sans-serif",
              "Times New Roman, Times, serif",
              "Trebuchet MS, Helvetica, sans-serif",
              "Verdana, Geneva, sans-serif",
              "Amazone",
              "Open Sans, sans-serif",
            ],
          },
          fontSize: {
            options: [10, 11, 12, 13, 14, 15, 16, 17, 18, 20, 22, 24, 26, 28, 32, 36, 40, 48, 60, 72],
            supportAllValues: true,
          },
          alignment: {
            options: ["left", "center", "right", "justify"],
          },
          // Khung "text bổ trợ thông tin" — áp class .info-box lên chính <p> đang
          // chọn (không phải <div>: nội dung bài luôn là <p>, style khai cho <div>
          // sẽ không bao giờ khớp block nào → cả dropdown "Styles" bị vô hiệu vĩnh
          // viễn). Style thật nằm ở globals.css (.ck-content .info-box /
          // .content-wrapper .info-box) để editor và trang public giống hệt nhau.
          style: {
            definitions: [
              {
                name: "Khung thông tin bổ trợ",
                element: "p",
                classes: ["info-box"],
              },
            ],
          },
          image: {
            styles: {
              options: [
                {
                  name: "alignLeft",
                  title: "Căn trái",
                  icon: "left",
                  className: "image-align-left",
                  modelElements: ["imageBlock", "imageInline"],
                },
                {
                  name: "alignCenter",
                  title: "Căn giữa",
                  icon: "center",
                  className: "image-align-center",
                  modelElements: ["imageBlock", "imageInline"],
                },
                {
                  name: "alignRight",
                  title: "Căn phải",
                  icon: "right",
                  className: "image-align-right",
                  modelElements: ["imageBlock", "imageInline"],
                },
                "side",
              ],
            },
            toolbar: [
              "imageStyle:alignLeft",
              "imageStyle:alignCenter",
              "imageStyle:alignRight",
              "|",
              "toggleImageCaption",
              "linkImage",
            ],
          },
          mediaEmbed: {
            previewsInData: true,
            providers: [
              {
                name: "YouTube",
                url: [
                  /^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([\w-]+)/,
                  /^https?:\/\/(?:www\.)?youtu\.be\/([\w-]+)/,
                  /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([\w-]+)/,
                ],
                html: (match: RegExpMatchArray) => {
                  const id = match[match.length - 1] || ""
                  if (!id) return ""
                  const src = `https://www.youtube.com/embed/${id}?rel=0`
                  return (
                    `<div style="position:relative;padding-bottom:56.25%;height:0;max-width:100%;">` +
                    `<iframe src="${src}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>` +
                    `</div>`
                  )
                },
              },
              {
                name: "Facebook",
                url: [
                  /^https?:\/\/(?:www|m)\.facebook\.com\/[^\/]+\/videos\/(\d+)/,
                  /^https?:\/\/(?:www|m)\.facebook\.com\/watch\/\?v=(\d+)/,
                  /^https?:\/\/fb\.watch\/[\w-]+\/?/,
                ],
                html: (match: RegExpMatchArray) => {
                  const href = encodeURIComponent(match[0])
                  const src = `https://www.facebook.com/plugins/video.php?href=${href}&show_text=false&width=1280`
                  return (
                    `<div style="position:relative;padding-bottom:56.25%;height:0;max-width:100%;">` +
                    `<iframe src="${src}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;overflow:hidden;" scrolling="no" frameborder="0" allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" allowfullscreen></iframe>` +
                    `</div>`
                  )
                },
              },
              {
                name: "TikTok",
                url: [/^https?:\/\/(?:www\.)?tiktok\.com\/@[\w\.-]+\/video\/(\d+)/],
                html: (match: RegExpMatchArray) => {
                  const videoId = match[1]
                  if (!videoId) return ""
                  return (
                    `<div style="position:relative;padding-bottom:56.25%;height:0;max-width:100%;">` +
                    `<iframe src="https://www.tiktok.com/embed/v2/${videoId}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>` +
                    `</div>`
                  )
                },
              },
              {
                name: "Instagram",
                url: [
                  /^https?:\/\/(?:www\.)?instagram\.com\/p\/([\w-]+)/,
                  /^https?:\/\/(?:www\.)?instagram\.com\/reel\/([\w-]+)/,
                  /^https?:\/\/(?:www\.)?instagram\.com\/tv\/([\w-]+)/,
                ],
                html: (match: RegExpMatchArray) => {
                  const shortcode = match[1]
                  if (!shortcode) return ""
                  return (
                    `<div style="position:relative;max-width:100%;overflow:hidden;">` +
                    `<iframe src="https://www.instagram.com/p/${shortcode}/embed/" style="width:100%;height:550px;border:0;overflow:hidden;" frameborder="0" allowtransparency="true" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"></iframe>` +
                    `</div>`
                  )
                },
              },
              {
                name: "Vimeo",
                url: /^https?:\/\/(?:www\.)?vimeo\.com\/(\d+)/,
                html: (match: RegExpMatchArray) => {
                  const id = match[1]
                  return id
                    ? `<div style="position:relative;padding-bottom:56.25%;height:0;"><iframe src="https://player.vimeo.com/video/${id}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`
                    : ""
                },
              },
              {
                name: "Dailymotion",
                url: /^https?:\/\/(?:www\.)?dailymotion\.com\/video\/([\w-]+)/,
                html: (match: RegExpMatchArray) => {
                  const id = match[1]
                  return id
                    ? `<div style="position:relative;padding-bottom:56.25%;height:0;"><iframe src="https://www.dailymotion.com/embed/video/${id}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe></div>`
                    : ""
                },
              },
              {
                name: "Twitter",
                url: [
                  /^https?:\/\/(?:www\.)?twitter\.com\/\w+\/status\/(\d+)/,
                  /^https?:\/\/(?:www\.)?x\.com\/\w+\/status\/(\d+)/,
                ],
                html: (match: RegExpMatchArray) => {
                  const tweetId = match[1]
                  if (!tweetId) return ""
                  const src = `https://platform.twitter.com/embed/Tweet.html?id=${tweetId}&dnt=true&theme=light`
                  return (
                    `<div style="max-width:550px;margin:0 auto;">` +
                    `<iframe src="${src}" style="width:100%;height:560px;border:0;" frameborder="0" scrolling="yes" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"></iframe>` +
                    `</div>`
                  )
                },
              },
              {
                name: "GenericPreview",
                url: /^https?:\/\/.+/,
                html: (match: RegExpMatchArray) => {
                  const href = match[0]
                  return `<div class="generic-embed" style="max-width:100%;border:1px solid #ddd;border-radius:8px;overflow:hidden;"><iframe src="${href}" style="width:100%;height:400px;border:0;" sandbox="allow-same-origin allow-scripts allow-popups allow-forms"></iframe></div>`
                },
              },
            ],
          },
          table: {
            contentToolbar: ["tableColumn", "tableRow", "mergeTableCells", "tableProperties", "tableCellProperties"],
            tableProperties: {},
            tableCellProperties: {},
          },
          link: {
            addTargetToExternalLinks: true,
            defaultProtocol: "https://",
          },
          ckbox: {
            tokenUrl: CKBOX_TOKEN_URL,
          },
          htmlSupport: {
            allow: [{ name: /.*/, attributes: true, classes: true, styles: true }],
          },
          htmlEmbed: {
            showPreviews: true,
            // Pass the raw HTML through unchanged. Actual XSS sanitization happens
            // server-side and on the public render layer, not inside the admin editor.
            sanitizeHtml: (html: string) => ({ html, hasChanged: false }),
          },
        }}
      />
    </div>
  )
}

export default CKEditorComponent
